import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Phone } from "lucide-react";
import { CtaButton } from "@/components/CtaButton";

/**
 * Full-screen mobile menu used by <SiteHeader/>.
 *
 * Lives in its own file so SiteHeader stays focused on the desktop header.
 * Receives open state + links + site info as props \u2014 no global state.
 */
export const SiteHeaderMobileMenu = ({ open, onClose, links, info }) => {
    // Lock body scroll while the menu is open
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.documentElement.style.overflow;
        document.documentElement.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            data-testid="site-header-mobile-menu"
            className="fixed inset-0 z-30 lg:hidden pt-16 sm:pt-20"
        >
            <button
                type="button"
                className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Close menu"
            />
            <div className="relative bg-cream border-b border-border/80 shadow-[0_24px_60px_rgba(30,35,32,0.18)] max-h-[calc(100vh-4rem)] overflow-y-auto">
                <nav className="mx-auto max-w-7xl px-4 sm:px-6 py-6 space-y-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`site-header-mobile-link-${
                                l.to.replace("/", "") || "home"
                            }`}
                            className={({ isActive }) =>
                                `block rounded-xl px-4 py-3 text-base font-medium ${
                                    isActive
                                        ? "bg-sand text-terracotta"
                                        : "text-foreground/85 hover:bg-sand/60"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="pt-4 mt-2 border-t border-border/70 grid grid-cols-1 gap-2">
                        <CtaButton to="/intake" variant="accent" icon={false}>
                            New client intake
                        </CtaButton>
                        <CtaButton to="/contact" variant="primary" icon={false}>
                            Book a consultation
                        </CtaButton>
                        {info?.phone_link && (
                            <a
                                href={info.phone_link}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-cream border border-border/80 px-4 py-3 text-sm text-moss"
                            >
                                <Phone className="h-4 w-4" />
                                {info?.phone}
                            </a>
                        )}
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default SiteHeaderMobileMenu;
